import { Navigate, Outlet } from 'react-router-dom'

import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { type FeatureFlag, useFeatureFlags } from '@/utils/feature-flag'

/**
 * FeatureFlagGuard Props
 */
type FeatureFlagGuardProps = {
  /**
   * The feature flag to check
   */
  flag: FeatureFlag
  /**
   * The path to redirect to if the feature flag is not enabled
   * @default '/home'
   */
  redirectTo?: string
}

/**
 * FeatureFlagGuard component that checks if a feature flag is enabled
 * and redirects to a specified path if it's not enabled.
 * If the feature flag is enabled, it renders the nested routes using Outlet.
 */
export const FeatureFlagGuard = ({ flag, redirectTo = '/home' }: FeatureFlagGuardProps) => {
  const featureFlags = useFeatureFlags()

  // Prevent redirecting during the initial load (hard refresh).
  // If we redirect before flags are loaded, users can get bounced to /home and never
  // return to the intended route.
  if (!featureFlags.data) {
    return <AnimatedLoader cancellable />
  }

  const featureFlagValue = featureFlags.data?.[flag] ?? ''

  // If the feature flag is not enabled, redirect to the specified path
  if (!featureFlagValue) {
    return <Navigate to={redirectTo} replace />
  }

  // If the feature flag is enabled, render the nested routes
  return <Outlet />
}
